// Financial calculation + formatting helpers

export const fmtMoney = (n, opts = {}) => {
  const val = Number(n || 0);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: opts.currency || "USD",
    maximumFractionDigits: opts.decimals ?? 0,
    minimumFractionDigits: opts.decimals ?? 0,
  }).format(val);
};

export const fmtPct = (n, d = 1) => `${Number(n || 0).toFixed(d)}%`;

export const fmtDate = (s) => {
  if (!s) return "";
  const str = String(s);
  // Parse plain YYYY-MM-DD as a LOCAL date (avoid UTC-midnight shifting it back a day).
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const dt = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(str);
  if (isNaN(dt)) return s;
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// Local calendar date as YYYY-MM-DD (never shifts across UTC like toISOString does).
export const todayISO = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
};

export const uid = () =>
  (crypto?.randomUUID ? crypto.randomUUID() : `id-${Date.now()}-${Math.random().toString(36).slice(2)}`);

const byType = (accounts, types) =>
  (accounts || []).filter((a) => types.includes(a.type)).reduce((s, a) => s + Number(a.balance || 0), 0);

export const toMonthly = (amount, frequency) => {
  const a = Number(amount || 0);
  switch (frequency) {
    case "weekly": return (a * 52) / 12;
    case "biweekly": return (a * 26) / 12;
    case "yearly": return a / 12;
    default: return a;
  }
};

export function computeMetrics(state) {
  const s = state || {};
  const accounts = s.accounts || [];
  const txns = s.transactions || [];
  const cash = byType(accounts, ["cash"]);
  const investments = byType(accounts, ["investment"]);
  const assets = byType(accounts, ["asset"]);
  const debt = Math.abs(byType(accounts, ["debt"]));
  const netWorth = cash + investments + assets - debt;

  const monthlyIncome = (s.incomeSources || []).reduce(
    (sum, x) => sum + toMonthly(x.amount, x.frequency), 0);
  const txIncome = txns.filter((t) => t.type === "income").reduce((a, t) => a + Number(t.amount || 0), 0);
  const txExpense = txns.filter((t) => t.type === "expense").reduce((a, t) => a + Math.abs(Number(t.amount || 0)), 0);
  const income = monthlyIncome || txIncome;
  const savingsRate = income > 0 ? ((income - txExpense) / income) * 100 : 0;

  return { cash, investments, assets, debt, netWorth, income, txIncome, txExpense, savingsRate };
}

// Deterministic 0-100 "Wealth & Stewardship" score derived from real Wealth data.
// Used to feed the Life Command Center's Wealth & Stewardship area.
export function computeStewardshipScore(state) {
  const s = state || {};
  const m = computeMetrics(s);
  const accounts = s.accounts || [];
  const bills = s.bills || [];
  const budgets = s.budgets || [];
  const clamp = (n) => Math.max(0, Math.min(100, Math.round(Number.isFinite(n) ? n : 0)));

  const billsMonthly = bills.reduce((a, b) => a + toMonthly(Number(b.amount || 0), b.frequency || "monthly"), 0);
  const budgetMonthly = budgets.reduce((a, b) => a + Number(b.budget || b.amount || 0), 0);
  const monthlyExpense = Math.max(billsMonthly + budgetMonthly, m.txExpense, m.income * 0.5, 500);

  // Emergency fund: cash months of expenses (6 months = 100)
  const months = monthlyExpense > 0 ? m.cash / monthlyExpense : 0;
  const fundScore = clamp((months / 6) * 100);

  // Credit utilization (auto from cards if limits exist, else credit.utilization)
  const cards = accounts.filter((a) => a.type === "debt" && a.subtype === "credit_card" && Number(a.creditLimit) > 0);
  const ccLimit = cards.reduce((x, a) => x + Number(a.creditLimit || 0), 0);
  const ccBal = cards.reduce((x, a) => x + Math.abs(Number(a.balance || 0)), 0);
  const util = ccLimit > 0 ? (ccBal / ccLimit) * 100 : Number(s.credit?.utilization || 0);
  const utilScore = clamp(100 - util * 1.6);

  // Savings rate (20% = 100)
  const savingsScore = clamp(m.savingsRate * 5);

  // Debt-to-asset ratio (lower is better)
  const totalAssets = m.cash + m.investments + m.assets;
  const denom = totalAssets + m.debt;
  const debtRatio = denom > 0 ? m.debt / denom : 0;
  const debtScore = clamp(100 - debtRatio * 130);

  // Goals progress
  const goals = s.goals || [];
  const goalProg = goals.length
    ? goals.reduce((a, g) => {
        if (Number.isFinite(g.progress)) return a + Number(g.progress);
        const target = Number(g.target || g.targetAmount || 0);
        const current = Number(g.current || g.saved || g.currentAmount || 0);
        return a + (target > 0 ? Math.min(100, (current / target) * 100) : 0);
      }, 0) / goals.length
    : null;

  const parts = [
    { key: "emergencyFund", v: fundScore, w: 0.25 },
    { key: "utilization", v: utilScore, w: 0.22 },
    { key: "savingsRate", v: savingsScore, w: 0.2 },
    { key: "debtLoad", v: debtScore, w: 0.18 },
  ];
  if (goalProg != null) parts.push({ key: "goals", v: clamp(goalProg), w: 0.15 });
  const totalW = parts.reduce((a, p) => a + p.w, 0);
  const score = clamp(parts.reduce((a, p) => a + p.v * p.w, 0) / totalW);

  return {
    score,
    hasData: accounts.length > 0 || bills.length > 0 || goals.length > 0,
    netWorth: m.netWorth,
    months: Math.round(months * 10) / 10,
    util: Math.round(util),
    savingsRate: Math.round(m.savingsRate),
    breakdown: {
      emergencyFund: fundScore,
      utilization: utilScore,
      savingsRate: savingsScore,
      debtLoad: debtScore,
      goals: goalProg != null ? clamp(goalProg) : null,
    },
  };
}

export function spentByCategory(txns, category) {
  return (txns || [])
    .filter((t) => t.type === "expense" && t.category === category)
    .reduce((a, t) => a + Math.abs(Number(t.amount || 0)), 0);
}

export function filterByPeriod(txns, period) {
  if (period === "all") return txns || [];
  const now = new Date();
  const start = new Date();
  if (period === "day") start.setDate(now.getDate() - 1);
  else if (period === "week") start.setDate(now.getDate() - 7);
  else if (period === "month") start.setMonth(now.getMonth() - 1);
  else if (period === "quarter") start.setMonth(now.getMonth() - 3);
  else if (period === "year") start.setFullYear(now.getFullYear() - 1);
  return (txns || []).filter((t) => new Date(t.date) >= start);
}

// ---- CSV parsing (client-side wizard) ----
export function parseCsv(text) {
  const rows = [];
  let field = "";
  let row = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ",") { row.push(field); field = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      row.push(field); field = "";
      if (row.some((x) => x.trim() !== "")) rows.push(row);
      row = [];
    } else field += c;
  }
  if (field !== "" || row.length) { row.push(field); if (row.some((x) => x.trim() !== "")) rows.push(row); }
  const headers = (rows[0] || []).map((h) => h.trim());
  return { headers, rows: rows.slice(1) };
}

const CATEGORY_KEYWORDS = {
  Groceries: ["grocery", "market", "whole foods", "trader", "aldi", "safeway", "kroger", "costco"],
  Dining: ["restaurant", "cafe", "coffee", "starbucks", "uber eats", "doordash", "grubhub", "mcdonald", "pizza"],
  Transport: ["uber", "lyft", "shell", "chevron", "gas", "fuel", "transit", "parking"],
  Shopping: ["amazon", "target", "walmart", "store", "shop"],
  Utilities: ["electric", "water", "internet", "comcast", "verizon", "at&t"],
  Housing: ["rent", "mortgage", "hoa"],
  Income: ["salary", "payroll", "deposit", "paycheck", "direct dep"],
  Entertainment: ["netflix", "spotify", "hulu", "disney", "cinema", "movie"],
  Health: ["pharmacy", "cvs", "walgreens", "doctor", "clinic", "gym"],
};

export function categorize(name, rules) {
  const low = (name || "").toLowerCase();
  // Learned rules take priority (merchant keyword -> category)
  if (rules && typeof rules === "object") {
    for (const [key, cat] of Object.entries(rules)) {
      if (key && low.includes(key)) return cat;
    }
  }
  for (const [cat, kws] of Object.entries(CATEGORY_KEYWORDS)) {
    if (kws.some((k) => low.includes(k))) return cat;
  }
  return "Other";
}

// Normalize a merchant name into a stable key (strip digits, punctuation, dates)
export function merchantKey(name) {
  return (name || "")
    .toLowerCase()
    .replace(/[0-9]+/g, " ")
    .replace(/[^a-z\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function parseAmount(s) {
  s = String(s || "").trim().replace(/,/g, "").replace(/\$/g, "");
  const neg = s.startsWith("(") && s.endsWith(")");
  s = s.replace(/[()]/g, "");
  const v = parseFloat(s);
  if (isNaN(v)) return 0;
  return neg ? -v : v;
}

export function normDate(s) {
  s = String(s || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s; // already a clean ISO date, keep as-is
  const d = new Date(s);
  if (!isNaN(d)) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }
  return s || todayISO();
}

// Build transactions from raw CSV rows + a column mapping (+ optional learned rules)
export function buildTransactions(rows, mapping, account, rules) {
  const out = [];
  for (const r of rows) {
    const get = (idx) => (idx != null && idx >= 0 && idx < r.length ? r[idx] : "");
    let amount = null;
    if (mapping.amount != null && mapping.amount >= 0 && String(get(mapping.amount)).trim() !== "") {
      amount = parseAmount(get(mapping.amount));
    } else if (mapping.debit != null || mapping.credit != null) {
      const d = mapping.debit != null && String(get(mapping.debit)).trim() ? Math.abs(parseAmount(get(mapping.debit))) : 0;
      const c = mapping.credit != null && String(get(mapping.credit)).trim() ? Math.abs(parseAmount(get(mapping.credit))) : 0;
      amount = c - d;
    }
    if (amount == null || isNaN(amount)) continue;
    const name = String(get(mapping.description) || "Transaction").trim() || "Transaction";
    out.push({
      id: uid(),
      date: normDate(get(mapping.date)),
      name,
      category: categorize(name, rules),
      account: account || "Imported",
      type: amount > 0 ? "income" : "expense",
      amount: Math.round(amount * 100) / 100,
    });
  }
  return out;
}

// Auto-detect column index by header keywords
export function detectColumn(headers, keys) {
  const lower = headers.map((h) => h.toLowerCase());
  for (const k of keys) {
    const idx = lower.findIndex((h) => h.includes(k));
    if (idx !== -1) return idx;
  }
  return -1;
}

// ---- Transaction categories ----
export const EXPENSE_CATEGORIES = [
  "Housing", "Groceries", "Dining", "Transport", "Utilities", "Phone & Internet",
  "Shopping", "Entertainment", "Health", "Insurance", "Travel", "Education",
  "Personal care", "Subscriptions", "Fitness", "Pets", "Gifts", "Childcare",
  "Fees & charges", "Taxes", "Investing", "Debt payment", "Charity", "Other",
];
export const INCOME_CATEGORIES = [
  "Salary", "Freelance", "Bonus", "Business", "Interest", "Dividends",
  "Rental income", "Refund", "Reimbursement", "Gift", "Side hustle", "Other",
];

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Apply (dir=+1) or reverse (dir=-1) a transaction's effect on account balances.
// income: signed positive amount; expense: signed negative amount; transfer: positive amount moved from->to.
// Convention: debt/liability accounts store the amount OWED as a positive number. Money arriving at a
// liability (a payment) REDUCES what you owe; money leaving it (a charge/cash advance) increases it.
export function adjustAccountsForTx(accounts, tx, dir) {
  if (!tx || !accounts) return accounts;
  const next = accounts.map((a) => ({ ...a }));
  const byId = {};
  next.forEach((a) => { byId[a.id] = a; });
  // cashIn > 0 means money arriving at the account.
  const applyCash = (a, cashIn) => {
    if (!a) return;
    const bal = Number(a.balance || 0);
    if (a.type === "debt") {
      // Treat as amount OWED regardless of how the balance was stored (positive or legacy negative).
      // A payment (cashIn > 0) reduces what you owe; a charge (cashIn < 0) increases it. Stored positive.
      a.balance = round2(Math.abs(bal) - cashIn);
    } else {
      a.balance = round2(bal + cashIn);
    }
  };
  if (tx.type === "transfer") {
    const amt = Math.abs(Number(tx.amount || 0)) * dir;
    applyCash(byId[tx.fromAccountId], -amt); // money leaves the "from" account
    applyCash(byId[tx.toAccountId], amt);    // money arrives at the "to" account
  } else if (tx.accountId && byId[tx.accountId]) {
    applyCash(byId[tx.accountId], Number(tx.amount || 0) * dir);
  }
  return next;
}

// ---- Recurring charge detection ----
// Groups expense transactions by merchant key; flags those appearing >= minCount
// with reasonably consistent amounts as candidate recurring bills.
export function detectRecurring(transactions, existingBills = [], minCount = 2) {
  const groups = {};
  (transactions || []).forEach((t) => {
    if (t.type !== "expense") return;
    const key = merchantKey(t.name);
    if (!key) return;
    (groups[key] = groups[key] || []).push(t);
  });
  const existingNames = new Set((existingBills || []).map((b) => merchantKey(b.name)));
  const candidates = [];
  for (const [key, txns] of Object.entries(groups)) {
    if (txns.length < minCount) continue;
    if (existingNames.has(key)) continue;
    const amounts = txns.map((t) => Math.abs(Number(t.amount || 0)));
    const avg = amounts.reduce((a, b) => a + b, 0) / amounts.length;
    // consistency: spread within 25% of avg
    const maxDev = Math.max(...amounts.map((a) => Math.abs(a - avg)));
    if (avg > 0 && maxDev / avg > 0.35) continue;
    // most common day of month
    const days = txns.map((t) => new Date(t.date).getDate()).filter((d) => !isNaN(d));
    const dueDay = days.length ? mode(days) : 1;
    // display name = most frequent original name
    const name = mode(txns.map((t) => t.name)) || txns[0].name;
    candidates.push({
      key,
      name,
      amount: Math.round(avg * 100) / 100,
      dueDay,
      count: txns.length,
      category: txns[0].category || "Other",
      isSubscription: false,
    });
  }
  return candidates.sort((a, b) => b.count - a.count);
}

function mode(arr) {
  const counts = {};
  let best = arr[0];
  let bestN = 0;
  for (const v of arr) {
    counts[v] = (counts[v] || 0) + 1;
    if (counts[v] > bestN) { bestN = counts[v]; best = v; }
  }
  return best;
}
// Returns { overdue: [...], dueSoon: [...] } for unpaid bills within the current month.
export function billReminders(bills, windowDays = 7) {
  const today = new Date();
  const day = today.getDate();
  const overdue = [];
  const dueSoon = [];
  (bills || []).forEach((b) => {
    if (b.paid) return;
    const due = Number(b.dueDay || 1);
    if (due < day) overdue.push(b);
    else if (due - day <= windowDays) dueSoon.push(b);
  });
  overdue.sort((a, b) => (a.dueDay || 0) - (b.dueDay || 0));
  dueSoon.sort((a, b) => (a.dueDay || 0) - (b.dueDay || 0));
  return { overdue, dueSoon };
}
